import IconButton from 'components/IconButton';
import MediaSlide from 'components/MediaSlide';
import Modal from 'components/Modal';
import MediaView from 'components/PostItem/MediaView';
import useEmblaCarousel from 'embla-carousel-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

type TChatMedia = { type: string; url: string; poster?: string };

type Props = {
  className?: string;
  onLoad?: () => void;
  listMedia: TChatMedia[];
  roundedTop?: string;
  roundedBottom?: string;
};

type ReturnPosition = { index: number };

type SwipeProps = {
  listMedia: TChatMedia[];
  onLoad?: () => void;
  onSelect: (index: number) => void;
  returnPosition: ReturnPosition | null;
};

function ChatMediaSwipe({
  listMedia,
  onLoad,
  onSelect,
  returnPosition,
}: SwipeProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    containScroll: 'trimSnaps',
    loop: false,
    slidesToScroll: 1,
  });
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    if (!emblaApi) return;
    const syncSelectedIndex = () => {
      setSelectedIndex(
        Math.min(emblaApi.selectedScrollSnap(), listMedia.length - 1),
      );
    };
    syncSelectedIndex();
    emblaApi.on('select', syncSelectedIndex).on('reInit', syncSelectedIndex);
    return () => {
      emblaApi
        .off('select', syncSelectedIndex)
        .off('reInit', syncSelectedIndex);
    };
  }, [emblaApi, listMedia]);

  useEffect(() => {
    if (!emblaApi || !returnPosition) return;
    emblaApi.scrollTo(returnPosition.index, true);
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi, returnPosition]);

  const currentIndex = Math.min(selectedIndex, listMedia.length - 1);

  return (
    <div className="p-2">
      <div className="relative">
        <div className="overflow-hidden rounded-lg" ref={emblaRef}>
          <div className="flex gap-8px touch-pan-y">
            {listMedia.map((media, index) => (
              <button
                key={index}
                type="button"
                className="relative flex-[0_0_100%] min-w-0 aspect-square overflow-hidden rounded-lg bg-neutral-100"
                onClick={() => onSelect(index)}
              >
                {media.type.startsWith('image/') ? (
                  <img
                    alt=""
                    src={media.url}
                    className="h-full w-full object-cover"
                    onLoad={onLoad}
                  />
                ) : (
                  <>
                    {media.poster ? (
                      <img
                        alt=""
                        src={media.poster}
                        className="h-full w-full object-cover"
                        onLoad={onLoad}
                      />
                    ) : (
                      <video
                        src={`${media.url}#t=0.1`}
                        className="h-full w-full object-cover"
                        preload="metadata"
                        muted
                        playsInline
                        onLoadedData={onLoad}
                      />
                    )}
                    <span className="absolute inset-0 flex items-center justify-center bg-black/10">
                      <span className="flex h-[44px] w-[44px] items-center justify-center rounded-full bg-black/60">
                        <span className="ml-1 h-0 w-0 border-y-[8px] border-l-[14px] border-y-transparent border-l-white" />
                      </span>
                    </span>
                  </>
                )}
              </button>
            ))}
          </div>
        </div>
        <span className="absolute right-2 top-2 rounded-full bg-black/60 px-2 text-[10px] leading-5 text-white">
          {`${currentIndex + 1} / ${listMedia.length}`}
        </span>
      </div>
      <div className="mt-2 flex justify-center gap-6px">
        {listMedia.map((_, index) => (
          <span
            key={index}
            className={`h-8px w-8px flex-none rounded-full ${
              index === currentIndex ? 'bg-primary' : 'bg-neutral-300'
            }`}
          />
        ))}
      </div>
    </div>
  );
}

function ChatMediaView({
  className,
  onLoad,
  listMedia,
  roundedTop,
  roundedBottom = 'rounded-b-xl',
}: Props) {
  // State
  const [selectMediaIndex, setSelectMediaIndex] = useState<number>(-1);
  const [returnPosition, setReturnPosition] = useState<ReturnPosition | null>(
    null,
  );
  const fullScreenIndexRef = useRef(0);

  // Callbacks
  const handleSelect = useCallback((index: number) => {
    fullScreenIndexRef.current = index;
    setSelectMediaIndex(index);
  }, []);
  const handleSelectedIndexChange = useCallback((index: number) => {
    fullScreenIndexRef.current = index;
  }, []);
  const handleClose = useCallback(() => {
    setReturnPosition({ index: fullScreenIndexRef.current });
    setSelectMediaIndex(-1);
  }, []);

  return (
    <>
      {listMedia?.length > 1 ? (
        <ChatMediaSwipe
          listMedia={listMedia}
          onLoad={onLoad}
          onSelect={handleSelect}
          returnPosition={returnPosition}
        />
      ) : (
        listMedia?.map((media, index) => (
          <MediaView
            key={index}
            className={`${className} ${index === 0 && roundedTop} ${
              index === listMedia?.length - 1 && roundedBottom
            }`}
            {...media}
            onClick={() => setSelectMediaIndex(index)}
            onLoad={onLoad}
            videoOptions={{ controls: false, isAutoPlayVideo: true }}
          />
        ))
      )}
      <Modal open={selectMediaIndex > -1} onClose={handleClose}>
        {selectMediaIndex > -1 && (
          <div
            className="h-svh w-full flex items-center overflow-hidden"
            onClick={handleClose}
          >
            <IconButton
              icon="close"
              transparent
              className="fixed top-0 left-0 z-50 m-4"
              onClick={(e) => {
                e.stopPropagation();
                handleClose();
              }}
              iconColor="white"
            />
            <MediaSlide
              data={listMedia}
              selectedMediaIndex={selectMediaIndex}
              onSelectedIndexChange={handleSelectedIndexChange}
            />
          </div>
        )}
      </Modal>
    </>
  );
}

export default ChatMediaView;
